import type {
    AudioFileInfo,
    CoreState,
    InputFile,
    ResourceForType,
    ResourceIDToResource,
    ResourcesService,
    ShowUserNotification,
} from "@/client/core/Core.api";
import type {
    LabsResource,
    LabsResourceID,
    LabsResourceMetadata,
    LabsResourceType,
} from "@/client/core/platform-types/labs.api";
import {
    parseAndMigrateAudioMetadata,
    parseAndMigrateJsonMetadata,
    parseAndMigrateMidiMetadata,
} from "@/client/core/platform-types/labs-metadata-migration";
import { createResourceMetadata } from "@/client/core/services/resource-metadata";
import { downloadFile } from "@/client/utils/downloadFile";
import { showError } from "@/client/utils/error-handling";
import {
    filterAudioResource,
    filterJsonResource,
    filterMidiResource,
} from "@/client/utils/filterResources";

export interface StoredLabsResource {
    readonly id: LabsResourceID;
    readonly metadata: LabsResourceMetadata;
    readonly file: Blob;
}

const kDbName = "labs-live-core";
const kDbVersion = 1;
const kStoreName = "audio-resources";

const openDB = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = indexedDB.open(kDbName, kDbVersion);
        request.onupgradeneeded = () => {
            const store = request.result.createObjectStore(kStoreName, {
                keyPath: ["namespace", "id.value"],
            });
            store.createIndex("by-namespace", "namespace");
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
    });

const putResourcesInDB = (
    db: IDBDatabase,
    namespace: string,
    resources: LabsResource[],
): Promise<void> =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(kStoreName, "readwrite");
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("Failed to write to IndexedDB"));
        const store = tx.objectStore(kStoreName);
        for (const resource of resources) {
            store.put({
                namespace,
                id: resource.id,
                metadata: resource.metadata,
                file: resource.file,
            });
        }
        tx.commit();
    });

const deleteResourcesInDB = (
    db: IDBDatabase,
    namespace: string,
    ids: readonly LabsResourceID[],
): Promise<void> =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(kStoreName, "readwrite");
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("Failed to delete from IndexedDB"));
        const store = tx.objectStore(kStoreName);
        for (const id of ids) {
            store.delete([namespace, id.value]);
        }
        tx.commit();
    });

const getResourceInDB = (
    db: IDBDatabase,
    namespace: string,
    id: LabsResourceID,
): Promise<StoredLabsResource | undefined> =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(kStoreName, "readonly");
        const request = tx.objectStore(kStoreName).get([namespace, id.value]);
        request.onsuccess = () => {
            resolve(
                request.result === undefined ? undefined : (request.result as StoredLabsResource),
            );
        };
        request.onerror = () => reject(request.error ?? new Error("Failed to read from IndexedDB"));
    });

/**
 * One stored resource from any app's namespace, read straight from the
 * database. Website resources belong to the app that stored them; this is
 * for the one case where something global (the Sounds dock's seed) has to
 * follow the user from one app to the next and be re-added there.
 */
export const readStoredResource = async (
    namespace: string,
    id: LabsResourceID,
): Promise<StoredLabsResource | undefined> => {
    const db = await openDB();
    try {
        return await getResourceInDB(db, namespace, id);
    } finally {
        db.close();
    }
};

const getAllResourcesInDB = (db: IDBDatabase, namespace: string): Promise<StoredLabsResource[]> =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(kStoreName, "readonly");
        const request = tx.objectStore(kStoreName).index("by-namespace").getAll(namespace);
        request.onsuccess = () => resolve(request.result as StoredLabsResource[]);
        request.onerror = () => reject(request.error ?? new Error("Failed to read from IndexedDB"));
    });

export const createResourcesServiceForWebsite = (
    namespace: string,
    state: CoreState,
    notifyResources: (resources: ResourceIDToResource) => void,
    showUserNotification: ShowUserNotification,
): ResourcesService => {
    const audioContext = new AudioContext();
    const dbPromise = openDB().catch((reason: unknown) =>
        showError(showUserNotification, "Loading Resources", reason),
    );

    const requestAllResources: ResourcesService["requestAllResources"] = () => {
        return dbPromise
            .then(async (db) => {
                if (db === undefined) {
                    return [];
                }
                const stored = await getAllResourcesInDB(db, namespace);
                for (const record of stored) {
                    const filenameOnDisk = record.metadata.filenameOnDisk;
                    let metadata: LabsResourceMetadata | Error;
                    switch (record.id.type) {
                        case "wav":
                            metadata = parseAndMigrateAudioMetadata(
                                record.metadata,
                                filenameOnDisk,
                            );
                            break;
                        case "mid":
                            metadata = parseAndMigrateMidiMetadata(record.metadata, filenameOnDisk);
                            break;
                        case "json":
                            metadata = parseAndMigrateJsonMetadata(record.metadata, filenameOnDisk);
                            break;
                    }
                    if (metadata instanceof Error) {
                        showError(showUserNotification, "Loading Resources", metadata);
                        continue;
                    }
                    const file = new File([record.file], metadata.originalFilename, {
                        type: record.file.type,
                    });
                    state.resources.set(record.id.value, { id: record.id, metadata, file });
                }
                notifyResources(state.resources);
                const result = Array.from(state.resources.values());
                return result;
            })
            .catch((reason: unknown) => {
                showError(showUserNotification, "Loading Resources", reason);
                return [];
            });
    };

    const addFile = async (input: InputFile): Promise<LabsResource> => {
        const idValue = crypto.randomUUID();
        let audioInfo: AudioFileInfo | undefined;

        if (input.type === "wav") {
            const arrayBuffer = await input.file.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            audioInfo = {
                duration_s: audioBuffer.duration,
                sampleRate: audioBuffer.sampleRate,
                numberOfChannels: audioBuffer.numberOfChannels,
                bpm: null,
            };
        }

        const metadata = createResourceMetadata(input, idValue, audioInfo);
        const resource: LabsResource = {
            id: { value: idValue, type: input.type },
            file: input.file,
            metadata,
        };

        state.resources.set(resource.id.value, resource);
        notifyResources(state.resources);

        const db = await dbPromise;
        if (db !== undefined) {
            await putResourcesInDB(db, namespace, [resource]).catch((reason: unknown) =>
                showError(showUserNotification, "Saving Resources", reason),
            );
        }

        return resource;
    };

    const addFiles: ResourcesService["addFiles"] = async <T extends LabsResourceType>(
        files: InputFile<T>[],
    ) => {
        if (files.length === 0) {
            return [] as ResourceForType<T>[];
        }
        const resources = await Promise.all(files.map(addFile));
        const inputTypes = new Set<LabsResourceType>(files.map((f) => f.type));
        return resources.filter((r): r is ResourceForType<T> => inputTypes.has(r.id.type));
    };

    const deleteResources: ResourcesService["deleteResources"] = async (ids) => {
        for (const id of ids) {
            state.resources.delete(id.value);
        }
        notifyResources(state.resources);
        const db = await dbPromise;
        if (db !== undefined) {
            await deleteResourcesInDB(db, namespace, ids);
        }
    };

    const updateResourceMetadata: ResourcesService["updateResourceMetadata"] = async (
        id,
        update,
    ) => {
        const resource = state.resources.get(id.value);
        if (resource === undefined) {
            return;
        }
        const updated: LabsResource = {
            ...resource,
            metadata: { ...resource.metadata, ...update },
        };
        Object.assign(resource.metadata, update);
        state.resources.set(id.value, updated);
        notifyResources(state.resources);
        const db = await dbPromise;
        if (db !== undefined) {
            await putResourcesInDB(db, namespace, [updated]).catch((reason: unknown) =>
                showError(showUserNotification, "Saving Resources", reason),
            );
        }
    };

    const getAudioResource: ResourcesService["getAudioResource"] = (id) =>
        filterAudioResource(id, state.resources);

    const getMidiResource: ResourcesService["getMidiResource"] = (id) =>
        filterMidiResource(id, state.resources);

    const getJsonResource: ResourcesService["getJsonResource"] = (id) =>
        filterJsonResource(id, state.resources);

    const revealOnDisk: ResourcesService["revealOnDisk"] = (id) => {
        const resource = state.resources.get(id.value);
        if (resource === undefined) {
            return;
        }
        downloadFile(resource.file);
    };

    return {
        addFiles,
        getAudioResource,
        getMidiResource,
        getJsonResource,
        deleteResources,
        updateResourceMetadata,
        requestAllResources,
        revealOnDisk,
    };
};
